import {
  destroyPromoCampaigns,
  generateUniqueCodesPromoCampaigns,
  usePromoCampaigns,
} from '@gozoki/api'
import { Chip, Paper, TextField, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import { includesLowerCase } from '@gozoki/tools'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import type { PermissionTuple } from '~/utils/ability'
import { AuthorizedButtonLink, usePermissionGuard, withRoutePermissions } from '~/utils/rbac'
import Page from '../../../components/Page'
import GenerateUniqueUseCodeButton from '../../../components/buttons/GenerateUniqueUseCodeButton'
import { AuthorizedButton } from '../../../components/communication/AuthorizedButton'
import { ConfirmResourceDeletionButton } from '../../../components/communication/ConfirmDeletionButton'
import { useToast } from '../../../components/communication/Toast'
import SimpleDataGrid from '../../../components/containers/SimpleDataGrid'
import PlusIcon from '../../../components/icons/PlusIcon'
import { getErrorMessageWithSentry } from '../../../utils/sentry'
import { fonts } from '../../../utils/theme/fonts.theme'

const READ_CAMPAIGN: PermissionTuple = ['read', 'promo.temporary-campaigns.campaign']
const EDIT_CAMPAIGN: PermissionTuple = ['edit', 'promo.temporary-campaigns.campaign']

export const Route = createFileRoute('/_authenticated/promo-campaign/')({
  staticData: {
    permission: READ_CAMPAIGN,
  },
  component: PromoCampaignsPage,
})
withRoutePermissions(Route)

const promoCampaignColumnDefs: GridColDef[] = [
  {
    field: 'type',
    flex: 1,
    headerName: 'Type',
  },
  {
    field: 'name',
    flex: 1,
    headerName: 'Nom',
  },
  {
    field: 'isActive',
    flex: 1,
    headerName: 'Statut',
    renderCell: (field) => <Chip label={field.value ? 'Active' : 'Inactive'} />,
  },
]

function PromoCampaignsPage() {
  const navigate = Route.useNavigate()
  const [Toast, showToast] = useToast()

  const canEdit = usePermissionGuard(EDIT_CAMPAIGN).authorized

  const [promoCampaignSearch, setPromoCampaignSearch] = useState('')
  const [campaignsSelection, setCampaignsSelection] = useState<number[]>([])

  const promoCampaigns = usePromoCampaigns()

  // ****************************************************** //
  //           QUERY TO DATAGRID TRANSFORMATION             //
  // ****************************************************** //

  const promoCampaignsRows = useMemo(() => {
    if (promoCampaigns.data === undefined) {
      return []
    }
    return promoCampaigns.data.filter((promoCampaign) =>
      includesLowerCase(promoCampaign.name, promoCampaignSearch),
    )
  }, [promoCampaigns.data, promoCampaignSearch])

  // ****************************************************** //
  //                         HANDLERS                       //
  // ****************************************************** //

  const deleteCampaignMut = useMutation({
    mutationFn: destroyPromoCampaigns,
  })

  const handleDeleteCampaign = async () => {
    const deletionPromise = deleteCampaignMut.mutateAsync(campaignsSelection, {
      onError: (error) => {
        showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
        console.error(error)
      },
      onSuccess: () => {
        promoCampaigns.refetch()
      },
    })
    setCampaignsSelection([]) // Avoid disabled button flickering
    return deletionPromise
  }

  const generateUniqueCodeMut = useMutation({
    mutationFn: generateUniqueCodesPromoCampaigns,
  })

  const navigateAndUpdateCampaign = async () => {
    navigate({ search: { promoCampaignId: campaignsSelection[0] }, to: '/promo-campaign/new' })
  }

  const campaignsDisabled =
    campaignsSelection.length === 0 ||
    deleteCampaignMut.isPending ||
    generateUniqueCodeMut.isPending

  return (
    <Page>
      <Toast />
      <div style={{ display: 'flex' }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Campagnes promotionnelles
        </Typography>
        <AuthorizedButtonLink
          variant="contained"
          sx={{ marginRight: '16px' }}
          to="/promo-campaign/new"
        >
          <PlusIcon />
          Ajouter une campagne
        </AuthorizedButtonLink>
      </div>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={promoCampaignSearch}
            onChange={(e) => setPromoCampaignSearch(e.target.value)}
          />
          <div style={{ flexGrow: 1 }} />
          <ConfirmResourceDeletionButton
            permission={['delete', 'promo.temporary-campaigns.campaign']}
            handleResourceDeletion={handleDeleteCampaign}
            disabled={campaignsDisabled}
          />
          <AuthorizedButton
            permission={{ any: [EDIT_CAMPAIGN, READ_CAMPAIGN] }}
            variant="contained"
            sx={{ height: '36px' }}
            disabled={campaignsDisabled}
            onClick={navigateAndUpdateCampaign}
          >
            {canEdit ? 'Modifier' : 'Voir'}
          </AuthorizedButton>
          <GenerateUniqueUseCodeButton
            campaignsSelection={campaignsSelection}
            campaignsDisabled={campaignsDisabled}
            promoCampaigns={promoCampaigns}
            setCampaignsSelection={setCampaignsSelection}
            generateUniqueCodeMut={generateUniqueCodeMut}
            sx={{ height: '36px', marginLeft: '16px' }}
          />
        </div>
        <SimpleDataGrid
          columns={promoCampaignColumnDefs}
          rows={promoCampaignsRows}
          loading={promoCampaigns.isLoading}
          onRowSelectionModelChange={(newSelection) =>
            setCampaignsSelection(newSelection as number[])
          }
        />
      </Paper>
    </Page>
  )
}
